import { sumBy, truncate } from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';

import { LocaleService } from '@sm360/widget-commons-core-services';
import { PriceAmounts } from '@sm360/widget-commons-general';
import { CurrencyFormats, ModelYear, PurchaseMethods, Quote } from '@sm360/widget-commons-vehicle';
import { ACCESSORY_DESCRIPTION_MAX_LENGTH } from 'build-and-quote/constants/accessoryImage.js';

const AccessoryAmount = ({
    isLease,
    isParentHover,
    option,
    optionCategory,
    optionItems,
    paymentFrequencyLabel,
    selectedFinanceTerm,
    selectOption,
    taxesFactor,
    setAnalyticsValue,
}) => {
    const buttonClass = ['sr-button-outline-2', 'is-fluid'];
    if (isParentHover || option.selected) {
        buttonClass.push('is-hover');
    }
    const paymentFrequencyLabelBlock = <span className="sub">{paymentFrequencyLabel}</span>;
    let packageFormattedAmount = '';
    if (selectedFinanceTerm) {
        let { payment } = option.calculateItemAmounts(isLease, selectedFinanceTerm, taxesFactor);
        payment += sumBy(
            optionItems,
            (item) => item.calculateItemAmounts(isLease, selectedFinanceTerm, taxesFactor).payment
        );
        if (payment > 0) {
            packageFormattedAmount = (
                <p className="is-price">
                    <PriceAmounts price={LocaleService.l(payment, CurrencyFormats.CURRENCY_2)} />
                    {paymentFrequencyLabelBlock}
                </p>
            );
        } else {
            packageFormattedAmount = <p className="is-price">{LocaleService.t('optionsPackageSection.NO_FEE')}</p>;
        }
    } else {
        let { retail } = option.calculateItemAmounts(isLease, selectedFinanceTerm, taxesFactor);
        retail += sumBy(
            optionItems,
            (item) => item.calculateItemAmounts(isLease, selectedFinanceTerm, taxesFactor).retail
        );

        if (retail > 0) {
            packageFormattedAmount = (
                <p className="is-price">
                    <PriceAmounts price={LocaleService.l(retail, CurrencyFormats.CURRENCY_0)} />
                    {paymentFrequencyLabelBlock}
                </p>
            );
        } else {
            packageFormattedAmount = <p className="is-price">{LocaleService.t('optionsPackageSection.NO_FEE')}</p>;
        }
    }

    const optionChooseLabel = LocaleService.t(!option.selected ? 'optionSection.add' : 'optionSection.remove');
    const optionRequired = LocaleService.t('optionSection.required');
    const optionChooseIcon = !option.selected ? 'icon-plus' : 'icon-minus';

    const handleClick = () => {
        selectOption(optionCategory, option);
        if (!option.selected) setAnalyticsValue(option.label || option.description, option.description);
    };

    return (
        <div className="accessory_wrapper--content has-primary-background">
            <div className="accessoryCode sr-disclaimer is-light">
                {option.code.charAt(0) !== '_' ? option.code : ''}
            </div>
            <div className="accessoryName sr-text">
                {option.description.length < ACCESSORY_DESCRIPTION_MAX_LENGTH
                    ? option.description
                    : truncate(option.description, {
                          length: ACCESSORY_DESCRIPTION_MAX_LENGTH,
                          separator: ' ',
                      })}
            </div>
            <div className="accessory_wrapper--body">{packageFormattedAmount}</div>
            <div className="accessory_wrapper--footer">
                {option.isConfigurationOption ? (
                    <p className="is-price">{optionRequired}</p>
                ) : (
                    <button className={buttonClass.join(' ')} onClick={handleClick}>
                        <icon className={optionChooseIcon} />
                        <span>{optionChooseLabel}</span>
                    </button>
                )}
            </div>
        </div>
    );
};

AccessoryAmount.propTypes = {
    isLease: PropTypes.bool.isRequired,
    isParentHover: PropTypes.bool.isRequired,
    option: PropTypes.instanceOf(ModelYear.OptionModel).isRequired,
    optionCategory: PropTypes.instanceOf(Quote.QuoteItemCategoryModel).isRequired,
    optionItems: PropTypes.arrayOf(PropTypes.instanceOf(ModelYear.OptionModel)).isRequired,
    paymentFrequencyLabel: PropTypes.string.isRequired,
    selectedFinanceTerm: PropTypes.instanceOf(PurchaseMethods.TermModel).isRequired,
    selectOption: PropTypes.func.isRequired,
    taxesFactor: PropTypes.number.isRequired,
};

AccessoryAmount.displayName = 'AccessoryAmount';

export default AccessoryAmount;
